/**
 * InitiativeOwnerPicker — pick the delivery owner of an initiative: a single
 * person OR a team (user_management group). The two are mutually exclusive, so
 * the picker exposes one flat list (people + teams) and emits whichever side was
 * chosen, clearing the other. Powers the portfolio "group by owner" swimlanes.
 */
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { User as UserIcon, Users as UsersIcon, Check } from 'lucide-react';
import { cn } from '../../../lib/utils';
import { EntityPickerBase, BidiText } from '../../../shared/components/common';
import { filterAndSortBySearch } from '../../../shared/utils/searchScore';
import { useUsers } from '@/shared/users/hooks/useUsers';
import { useGroups } from '@/shared/users/hooks/useGroups';

export interface InitiativeOwner {
  owner_user_id: string | null;
  owner_team_id: string | null;
}

interface OwnerOption {
  /** Composite id so EntityPickerBase can key on one string: `user:<uuid>` / `team:<uuid>`. */
  id: string;
  kind: 'user' | 'team';
  name: string;
}

interface InitiativeOwnerPickerProps {
  ownerUserId: string | null;
  ownerTeamId: string | null;
  onChange: (owner: InitiativeOwner) => void;
  label?: string;
  disabled?: boolean;
  className?: string;
}

const USER_PREFIX = 'user:';
const TEAM_PREFIX = 'team:';

export function InitiativeOwnerPicker({
  ownerUserId,
  ownerTeamId,
  onChange,
  label,
  disabled,
  className,
}: InitiativeOwnerPickerProps) {
  const { t } = useTranslation(['softwareFactory', 'common']);
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');

  const { data: usersData } = useUsers();
  const { data: groupsData } = useGroups();

  // Teams first, then people — both flattened into one list with a kind tag.
  const options = useMemo<OwnerOption[]>(() => {
    const teams: OwnerOption[] = (groupsData?.data ?? []).map((g) => ({
      id: `${TEAM_PREFIX}${g.id}`,
      kind: 'team',
      name: g.name,
    }));
    // Owners are internal staff — exclude external (customer) users.
    const people: OwnerOption[] = (usersData?.data ?? [])
      .filter((u) => u.user_type !== 'external')
      .map((u) => ({
        id: `${USER_PREFIX}${u.id}`,
        kind: 'user' as const,
        name: u.full_name || u.email,
      }));
    return [...teams, ...people];
  }, [groupsData?.data, usersData?.data]);

  const filtered = useMemo(
    () => filterAndSortBySearch(options, search, (o) => o.name),
    [options, search],
  );

  const value = ownerTeamId
    ? `${TEAM_PREFIX}${ownerTeamId}`
    : ownerUserId
      ? `${USER_PREFIX}${ownerUserId}`
      : '';

  return (
    <EntityPickerBase<OwnerOption>
      value={value}
      items={filtered}
      getItemLabel={(o) => o.name}
      renderItem={(o, isSelected) => (
        <div className="flex w-full items-center gap-2">
          <Check className={cn('h-4 w-4 shrink-0', isSelected ? 'opacity-100' : 'opacity-0')} />
          {o.kind === 'team' ? (
            <UsersIcon className="h-4 w-4 shrink-0 text-muted-foreground" />
          ) : (
            <UserIcon className="h-4 w-4 shrink-0 text-muted-foreground" />
          )}
          <BidiText className="truncate">{o.name}</BidiText>
        </div>
      )}
      label={label}
      placeholder={t('softwareFactory:initiatives.noOwner')}
      searchPlaceholder={t('softwareFactory:initiatives.ownerSearchPlaceholder')}
      emptyMessage={t('softwareFactory:initiatives.ownerEmpty')}
      disabled={disabled}
      className={className}
      search={search}
      onSearchChange={setSearch}
      open={open}
      onOpenChange={(isOpen) => {
        setOpen(isOpen);
        if (!isOpen) setSearch('');
      }}
      onSelect={(id) => {
        if (id.startsWith(TEAM_PREFIX)) {
          onChange({ owner_user_id: null, owner_team_id: id.slice(TEAM_PREFIX.length) });
        } else {
          onChange({ owner_user_id: id.slice(USER_PREFIX.length), owner_team_id: null });
        }
        setOpen(false);
      }}
      allowClear
      onClear={() => {
        onChange({ owner_user_id: null, owner_team_id: null });
        setOpen(false);
      }}
      hideAddNew
      addNewLabel=""
      onAddNew={() => {}}
    />
  );
}
